import { useGetTourOfferSummary } from '~/hooks/apis/tours/useTourOffers';

import { Bundle } from '~/types/bundles/bundle';

import { brandName, regionName } from './regions';
import useBundleDepartureChecks from './useBundleDepartureChecks';
import useBundleMarkets from './useBundleMarkets';

export type BuilderStep = 'build' | 'departures' | 'prices' | 'approval' | 'content';

export const BUILDER_STEPS: ReadonlyArray<{ key: BuilderStep; label: string }> = [
  { key: 'build', label: 'Tour & hotel' },
  { key: 'departures', label: 'Departures & markets' },
  { key: 'prices', label: 'Prices' },
  { key: 'approval', label: 'Approval' },
  { key: 'content', label: 'Content' },
];

export type CheckState = 'done' | 'todo' | 'warning' | 'checking';

export interface ReadinessCheck {
  key: string;
  label: string;
  detail?: string;
  state: CheckState;
  /** Whether approval waits on it. A warning still lets the bundle be approved. */
  isBlocking: boolean;
  step: BuilderStep;
}

const pluralise = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

/** Everything the bundle needs before approval, and where each is fixed. */
export default function useBundleReadiness(bundle: Bundle) {
  const departureChecks = useBundleDepartureChecks(bundle);
  const markets = useBundleMarkets(bundle);
  const { tour, hotels, departures, isChecking, checkError, blocked } = departureChecks;
  const { data: tourOffer } = useGetTourOfferSummary(tour?.tour_offer_id);

  const isApproved = bundle.status === 'approved';
  const hasLtle = !!tourOffer?.isLtle;

  const checks: Array<ReadinessCheck> = [
    {
      key: 'tour',
      label: 'Base tour added',
      state: tour ? 'done' : 'todo',
      isBlocking: true,
      step: 'build',
    },
    {
      key: 'hotel',
      label: 'Flash hotel added',
      state: hotels.length > 0 ? 'done' : 'todo',
      isBlocking: true,
      step: 'build',
    },
    {
      key: 'markets',
      label: 'Where it sells',
      detail: markets.error
        ? "The tour or hotel couldn't be loaded. Reload to try again."
        : markets.sellsIn.length > 0
          ? `Sells in ${markets.sellsIn.map(regionName).join(', ')} under ${markets.sellsUnder.map(brandName).join(', ')}`
          : !markets.hasTour || !markets.hasHotel || markets.isLoading
            ? undefined
            : !markets.hasShared
              ? "The hotel isn't on sale anywhere the tour is scheduled, under the same brand"
              : 'None of the regions kept are on sale under a brand kept',
      state:
        !markets.hasTour || !markets.hasHotel
          ? 'todo'
          : markets.isLoading
            ? 'checking'
            : markets.error
              ? 'warning'
              : markets.sellsIn.length > 0
                ? 'done'
                : 'todo',
      isBlocking: true,
      step: 'departures',
    },
    {
      key: 'departures',
      label: 'Departures chosen',
      detail: departures.length > 0 ? pluralise(departures.length, 'departure') : undefined,
      state: departures.length > 0 ? 'done' : 'todo',
      isBlocking: true,
      step: 'departures',
    },
    {
      key: 'availability',
      label: 'Every departure can be sold',
      detail: checkError
        ? "Availability couldn't be checked"
        : blocked.length > 0
          ? `${pluralise(blocked.length, 'departure')} can't be sold as a single and a double`
          : undefined,
      state:
        departures.length === 0
          ? 'todo'
          : isChecking
            ? 'checking'
            : checkError || blocked.length > 0
              ? 'warning'
              : 'done',
      isBlocking: false,
      step: 'departures',
    },
  ];

  const blockers = checks.filter((check) => check.isBlocking && check.state !== 'done');

  const isStepComplete: Record<BuilderStep, boolean> = {
    build: checks.filter((check) => check.step === 'build').every((check) => check.state === 'done'),
    departures: checks.filter((check) => check.step === 'departures').every((check) => check.state === 'done'),
    prices: departures.length > 0,
    approval: isApproved,
    content: isApproved && hasLtle,
  };

  // The step that holds the first thing left to do. A draft with nothing blocking still reviews prices before approval.
  const nextStep: BuilderStep = isApproved ? (hasLtle ? 'content' : 'approval') : (blockers[0]?.step ?? 'prices');

  return {
    ...departureChecks,
    markets,
    checks,
    blockers,
    isApproved,
    hasLtle,
    isStepComplete,
    nextStep,
  };
}
